/**
 * Referral (09) — server response shapes (mirror of the `referral` module, CLOUD-only). The server
 * returns the standard { success, data } envelope; the api/useApiQuery layer unwraps `data`, so these
 * types describe the UNWRAPPED payload.
 *
 * BigInt ids serialize to JSON numbers/strings, so every id is typed `string | number` and normalized
 * to a string with `idStr()` before use as a key/param. Only the fields the screens read are typed; the
 * server may carry more keys (forward-compatible). Where a contract is uncertain we keep a field optional
 * and degrade gracefully rather than invent it.
 */

/** A normalized id — the wire carries either; callers compare/scope on the string form. */
export type Id = string | number;

/** Coerce a wire id to a stable string (BigInt-safe; never produces "[object Object]"). */
export function idStr(id: Id | null | undefined): string {
  return id == null ? "" : String(id);
}

// ── Lien (lab ↔ médecin link) ──────────────────────────────────────────────────────

/** The link lifecycle (enum LienStatut). invite → approuve | rejete; approuve → revoque. */
export type LienStatut = "invite" | "approuve" | "rejete" | "revoque";

// ── Analytics cards (also the per-médecin roster + the link-table source of truth) ──
//
// GET /referral/analytics → { cards: MedecinCard[] }. Each card joins the médecin projection with its
// current link status + its attributed-return count, so this single read drives BOTH the analytics tiles
// AND the link table (invite → approve → revoke) — there is no separate GET liens endpoint by design.

export interface MedecinCard {
  medecin_id: string;
  nom: string;
  novera_email: string;
  /** Current link status (null = no link yet). */
  lien_statut: LienStatut | null;
  /** Attributed prescriber-returns for this médecin. */
  retours_count: number;
}

export interface AnalyticsCards {
  cards: MedecinCard[];
}

// ── Retour (prescriber-return delivery tracking) ────────────────────────────────────

/** Delivery status of a return row (enum RetourStatut). */
export type RetourStatut = "en_attente" | "envoye" | "echec";

/** Delivery channel (enum CanalRetour). */
export type CanalRetour = "novera_dossier" | "email" | "queued";

/** A prescriber-return tracking row (GET /referral/retours?medecin_id= row). */
export interface RetourPrescripteur {
  id: Id;
  demande_analyse_id: Id;
  medecin_prescripteur_id: Id;
  compte_rendu_numero?: string | null;
  compte_rendu_version: number;
  valide_parameters_count: number;
  has_critique_result: boolean;
  delivery_channel: CanalRetour | string;
  statut: RetourStatut | string;
  returned_at?: string | null;
  created_at?: string;
}

// ── Discount candidate (REF-2 resolver) ─────────────────────────────────────────────

/** One referral discount candidate, or null (clean absence — never an error). */
export interface RemiseCandidate {
  pct: number;
  type: "referral";
  origine_id: string;
}

export interface CandidateResponse {
  candidate: RemiseCandidate | null;
}

// ── Mutation results ────────────────────────────────────────────────────────────────

export interface InviteResult {
  lien_id: Id;
  medecin_id: Id;
  callback_token: string;
}

export interface DecisionResult {
  lien_id: Id;
  statut: "approuve" | "rejete";
}

export interface RevokeResult {
  lien_id: Id;
  statut: "revoque";
  codes_desactives: number;
}

export interface CodeCreated {
  code_id: Id;
  code: string;
}

export interface CodeToggled {
  code_id: Id;
  statut: "actif" | "inactif";
}
